/**
 * 段内密度曲线 SVG —— 显示考生位置在分数分布上的相对位置。
 */
import { useMemo } from 'react'
import { buildDensityModel, type DensityMethod } from '@/domain/density'
import type { YearPolicy } from '@/domain/types'

interface Props {
  policy: YearPolicy
  score: number
  method?: DensityMethod
  height?: number
}

export function DensityCurve({ policy, score, method = 'whittaker', height = 140 }: Props) {
  const { path, viewBox, lo, hi, markerX, peakDensity } = useMemo(() => {
    const model = buildDensityModel(policy, method)
    const curve = model.curve()
    if (curve.length === 0 || policy.top300_line === null || policy.qualification_line === null) {
      return { path: '', viewBox: '0 0 100 100', lo: 0, hi: 100, markerX: 0, peakDensity: 0 }
    }
    const _lo = policy.qualification_line
    const _hi = policy.top300_line
    const w = 300
    const h = height
    const peak = Math.max(...curve.map((p) => p.density))
    const xOf = (s: number) => ((s - _lo) / (_hi - _lo)) * w
    const yOf = (d: number) => h - (d / peak) * h * 0.85 - 8

    // 用平滑路径
    let d = `M ${xOf(curve[0].score).toFixed(1)} ${yOf(curve[0].density).toFixed(1)}`
    for (let i = 1; i < curve.length; i++) {
      d += ` L ${xOf(curve[i].score).toFixed(1)} ${yOf(curve[i].density).toFixed(1)}`
    }
    // 闭合到底
    d += ` L ${w} ${h} L 0 ${h} Z`

    const mark = xOf(Math.max(_lo, Math.min(_hi - 1, score)))
    return {
      path: d,
      viewBox: `0 0 ${w} ${h}`,
      lo: _lo,
      hi: _hi,
      markerX: mark,
      peakDensity: peak
    }
  }, [policy, score, method, height])

  if (!path) return null

  return (
    <div className="w-full">
      <svg viewBox={viewBox} className="w-full h-auto block" preserveAspectRatio="none">
        <defs>
          <linearGradient id="dc-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path d={path} fill="url(#dc-fill)" stroke="#1e40af" strokeWidth="1.5" />
        {/* 考生标记 */}
        <line
          x1={markerX}
          y1={4}
          x2={markerX}
          y2={height - 4}
          stroke="#dc2626"
          strokeWidth="1.5"
          strokeDasharray="3,3"
        />
        <circle cx={markerX} cy={12} r={5} fill="#dc2626" />
      </svg>
      <div className="flex justify-between text-xs text-ink-400 mt-1 tabular-nums">
        <span>{lo} 分（资格线）</span>
        <span className="text-ink-600">你的位置：{score} 分</span>
        <span>{hi} 分（前 300 线）</span>
      </div>
      <div className="text-xs text-ink-400 mt-0.5">
        该段竞争最激烈处约 {peakDensity.toFixed(1)} 人/分，红线为你的位置
      </div>
    </div>
  )
}
